import { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { CheckOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttButton,
    SttDialog,
    SttFormDate,
    SttFormSelect,
    SttFormText,
    SttFormTextarea,
    SttFormTime,
    SttSelectAutocomplete,
    SttSelectWeekdays,
} from '../../../sporttia/all';
import type { DialogButton } from '../../../sporttia/dialogs/SttDialog';
import { SttFullDialog } from '../../../sporttia/dialogs/SttFullDialog';
import { SttFormAttendance } from '../../../sporttia/forms/SttFormAttendance';
import { useLoader } from '../../../lib/hooks';
import type { Activity as ActivityType } from '../../../types/models/Activity';
import { PERMISSIONS } from '../../../lib/permissions';

type Props = {
    open: boolean;
    item: Class | null;
    activity?: ActivityType;
    onSave?: () => void;
    onClose?: () => void;
    onGoToActivity?: () => void;
    availableTeachers?: Teacher[];
};

type Teacher = {
    id: number;
    name: string;
};

type Facility = {
    id: number;
    name: string;
};

type Class = {
    id?: number;
    name?: string;
    notes?: string;
    status?: string;
    facility?: {
        id: number;
        name: string;
    } | null;
    teacher?: {
        id: number;
        name: string;
    } | null;
    ini?: string;
    end?: string;
    weekdays?: number[];
    activity?: {
        id: number;
        name?: string;
    };
};

/**
 * ActivityClassDialog - Dialog to add / modify classes to an Activity
 * bool open - dialog is open/closed
 * Object item - The class itself. If it contains an id, it's an existing class
 * Object activity - The parent Activity, if any
 * Function onSave - callback when changes are applied or one or more classes are created
 * Function onClose - closing callback
 * Array teachers - array of teaches from this sports center
 */
export default function ActivityClassDialog({
    open,
    item,
    activity,
    onSave,
    onClose,
    onGoToActivity,
    availableTeachers = [],
}: Props): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    // 'class' is a reserved keyword in Javascript, so we use theClass instead
    const [theClass, setTheClass] = useState<Class | null>(item);
    const [teachers, setTeachers] = useState<Teacher[]>(availableTeachers);
    // Facilities from this sc
    const [facilities, setFacilities] = useState<Facility[]>([]);
    const [dialog, setDialog] = useState(false);
    const [occupationsClassesLoading, setOccupationsClassesLoading] = useState<
        'idle' | 'loading'
    >('idle');

    // Derive activity from prop or from item
    const derivedActivity = useMemo(() => {
        if (activity) return activity;
        if (theClass?.activity?.id) {
            return {
                id: theClass.activity.id,
                name: theClass.activity.name || theClass.name,
            } as ActivityType;
        }
        return undefined;
    }, [activity, theClass]);

    // Load class details to get activity info
    function loadClassDetails(classId: number): void {
        cxt?.api('GET', `/classes/${classId}`, {
            success: (response: Record<string, unknown>) => {
                // Try different possible response structures
                const classData = (response.class ||
                    response.activityClass ||
                    response) as Class | undefined;
                if (classData?.activity) {
                    setTheClass((prev) =>
                        prev
                            ? {
                                  ...prev,
                                  activity: classData.activity,
                              }
                            : prev,
                    );
                }
            },
        });
    }

    // load facilities from API
    function loadFacilities(): void {
        cxt?.api('GET', `/scs/${cxt?.sc?.id}/facilities`, {
            params: {
                rows: 1000,
                page: 1,
                status: 'ACTIVE,ONLY_SC',
            },
            success: (response: { rows?: Facility[] }) => {
                if (response.rows) {
                    setFacilities(response.rows);
                }
            },
        });
    }

    function loadTeachers(): void {
        cxt?.api('GET', `/scs/${cxt?.sc?.id}/teachers`, {
            params: { page: 1, rows: 1000 },
            success: (response: { rows?: Teacher[] }) => {
                if (response.rows) {
                    setTeachers(response.rows);
                }
            },
        });
    }

    // startup
    useEffect(() => {
        loadFacilities();
        if (teachers.length === 0) {
            loadTeachers();
        }
    }, []);

    // if the 'item' prop changes, set is as theClass
    useEffect(() => {
        if (item && !item.name && activity?.name) {
            setTheClass({ ...item, name: activity.name });
        } else {
            setTheClass(item);
        }
    }, [item, activity]);

    // Load class details if we have an ID but no activity info
    useEffect(() => {
        if (open && item?.id && !activity && !item?.activity) {
            loadClassDetails(item.id);
        }
    }, [open, item?.id, activity, item?.activity]);

    // apply 'value' to field 'name'
    function handleChange({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        setTheClass({ ...theClass, [name]: value } as Class);
    }

    // Select a facility from the list
    function setFacility(id: number | null): void {
        const facility = facilities.find((f: any) => f.id === id);

        if (facility) {
            setTheClass({
                ...theClass,
                facility: { id: facility.id, name: facility.name },
            } as Class);
        } else {
            setTheClass({ ...theClass, facility: null } as Class);
        }
    }

    // Select a teacher from the list
    function setTeacher(id: number | null): void {
        const teacher = teachers.find((t: any) => t.id === id);

        if (teacher) {
            setTheClass({
                ...theClass,
                teacher: { id: teacher.id, name: teacher.name },
            } as Class);
        } else {
            setTheClass({ ...theClass, teacher: null } as Class);
        }
    }

    /*
    Set the date(s).

        If the class already exists, both ini and end refer to the same day, and the TIME part of the date refers to the class duration.
            For example: 2020-04-07 10:00:00 - 2020-04-07 12:00:00 Means a class on 2020-04-07 that runs from 10:00 to 12:00

        If the class doesn't exist (has no id), then ini and end function both as start and end of the date range, as well as start time and end time
            For example: 2020-04-07 10:00:00 - 2020-05-07 12:00:00 means that classes will be created from 2020-04-07 to 2020-05-07 and they
            will each run from 10:00 to 12:00

    Weird, but that's how it is.
    */
    function setDate({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        if (!theClass) return;

        const currentFieldValue = theClass[name as keyof Class] as
            | string
            | undefined;
        const current = currentFieldValue
            ? parseISO(currentFieldValue)
            : new Date();

        if (theClass.id) {
            const formattedDate = format(
                parseISO(value as string),
                'yyyy-MM-dd',
            );
            const formattedTime = format(current, 'HH:mm:ss');
            setTheClass(
                (prev) =>
                    ({
                        ...prev,
                        ini: `${formattedDate} ${formattedTime}`,
                        end: `${formattedDate} ${formattedTime}`,
                    }) as Class,
            );
        } else {
            const formattedDate = format(
                parseISO(value as string),
                'yyyy-MM-dd',
            );
            const formattedTime = format(current, 'HH:mm:ss');
            setTheClass(
                (prev) =>
                    ({
                        ...prev,
                        [name]: `${formattedDate} ${formattedTime}`,
                    }) as Class,
            );
        }
    }

    // Set the time part of the 'name' date
    function setTime({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void {
        if (!name) return;
        if (!theClass) return;

        const currentValue = theClass[name as keyof Class] as string;
        const current = parseISO(currentValue);
        setTheClass({
            ...theClass,
            [name]: `${format(current, 'yyyy-MM-dd')} ${value}`,
        });
    }

    // Show success message, then call both onSave and onClose
    function saveAndClose(): void {
        cxt?.showMessage('S', t('OperationSuccessful'));
        onSave?.();
        onClose?.();
    }

    // Save via API. Uses PUT or POST depending on whether the class has an id
    function saveClass(): void {
        if (!theClass) return;

        // Validate that name is not empty
        if (!theClass.name || theClass.name.trim() === '') {
            cxt?.showMessage('E', t('validation.nameRequired'));
            return;
        }

        const ini = format(parseISO(theClass.ini!), "yyyy-MM-dd'T'HH:mm:ss");
        const end = format(parseISO(theClass.end!), "yyyy-MM-dd'T'HH:mm:ss");
        setOccupationsClassesLoading('loading');
        if (theClass.id) {
            cxt?.api('PUT', `/classes/${theClass.id}`, {
                params: {
                    name: theClass.name,
                    notes: theClass.notes,
                    status: theClass.status,
                    idFacility: theClass.facility
                        ? theClass.facility.id
                        : undefined,
                    idTeacher: theClass.teacher
                        ? theClass.teacher.id
                        : undefined,
                    ini,
                    end,
                },
                success: () => {
                    saveAndClose();
                    setOccupationsClassesLoading('idle');
                },
                error: (errorResponse: unknown) => {
                    setOccupationsClassesLoading('idle');
                    const err = errorResponse as { error?: { msg?: string } };
                    if (err?.error?.msg) {
                        cxt?.showMessage('E', err.error.msg);
                    }
                },
            });
        } else {
            cxt?.api('POST', `/classes`, {
                params: {
                    name: theClass.name,
                    notes: theClass.notes,
                    status: theClass.status,
                    idFacility: theClass.facility
                        ? theClass.facility.id
                        : undefined,
                    idTeacher: theClass.teacher
                        ? theClass.teacher.id
                        : undefined,
                    idActivity: activity ? activity.id : undefined,
                    ini,
                    end,
                    weekdays: theClass.weekdays,
                },
                success: () => {
                    saveAndClose();
                    setOccupationsClassesLoading('idle');
                },
                error: (errorResponse: unknown) => {
                    setOccupationsClassesLoading('idle');
                    const err = errorResponse as { error?: { msg?: string } };
                    if (err?.error?.msg) {
                        cxt?.showMessage('E', err.error.msg);
                    }
                },
            });
        }
    }

    // Delete via API
    function deleteClass(): void {
        if (!theClass?.id) return;

        cxt?.api('DELETE', `/classes/${theClass.id}`, {
            confirmation: true,
            success: () => {
                saveAndClose();
            },
        });
    }
    const [, loader] = useLoader([occupationsClassesLoading]);

    if (!theClass) {
        return null;
    }

    // Build buttons array - must be after theClass check
    const buttons: DialogButton[] = [
        {
            type: 'save',
            onClick: saveClass,
            permission: PERMISSIONS.ACTIVITY.MODIFY,
        },
    ];

    // If the class has an id, add delete button
    if (theClass.id) {
        buttons.push({
            type: 'delete',
            onClick: deleteClass,
            permission: PERMISSIONS.ACTIVITY.MODIFY,
        });
    }

    // Add attendances button only if we have activity info
    if (theClass.id && derivedActivity) {
        buttons.push({
            component: (
                <SttButton
                    icon={<CheckOutlined />}
                    caption={t('Assistences')}
                    onClick={() => setDialog(true)}
                />
            ),
        });
    }

    return (
        <>
            <SttDialog
                title={t('Class')}
                subtitle={activity && activity.name}
                maxWidth="sm"
                open={open}
                buttons={buttons}
                onClose={onClose}
                onPressSubtitle={onGoToActivity}
                content={
                    <>
                        {loader}
                        <div>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12 }}>
                                    <SttFormText
                                        name="name"
                                        caption={t('Name')}
                                        defVal={theClass.name}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <SttFormTextarea
                                        name="notes"
                                        caption={t('Notes')}
                                        defVal={theClass.notes}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <SttFormSelect
                                        caption={t('Status')}
                                        name="status"
                                        defVal={theClass.status}
                                        options={[
                                            {
                                                caption: t('Active'),
                                                value: 'ACTIVE',
                                            },
                                            {
                                                caption: t('Inactive'),
                                                value: 'INACTIVE',
                                            },
                                        ]}
                                        onChange={handleChange}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <SttSelectAutocomplete
                                        name="facility"
                                        caption={t('Facility')}
                                        options={facilities}
                                        defVal={
                                            theClass.facility
                                                ? theClass.facility.id
                                                : null
                                        }
                                        onChange={
                                            (({
                                                value,
                                            }: {
                                                name: string;
                                                value: unknown;
                                            }) =>
                                                setFacility(
                                                    value as number | null,
                                                )) as any
                                        }
                                    />
                                </Grid>
                                <Grid
                                    size={{ xs: 12, sm: theClass.id ? 6 : 12 }}
                                >
                                    {teachers && (
                                        <SttSelectAutocomplete
                                            name="teacher"
                                            caption={t('Teacher')}
                                            options={teachers}
                                            defVal={
                                                theClass.teacher
                                                    ? theClass.teacher.id
                                                    : null
                                            }
                                            onChange={
                                                (({
                                                    value,
                                                }: {
                                                    name: string;
                                                    value: unknown;
                                                }) =>
                                                    setTeacher(
                                                        value as number | null,
                                                    )) as any
                                            }
                                        />
                                    )}
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <SttFormDate
                                        name="ini"
                                        caption={t('Date')}
                                        defVal={theClass.ini}
                                        onChange={setDate}
                                    />
                                </Grid>
                                {!theClass.id && (
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <SttFormDate
                                            name="end"
                                            caption={t('End')}
                                            defVal={theClass.end}
                                            onChange={setDate}
                                        />
                                    </Grid>
                                )}
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <SttFormTime
                                        name="ini"
                                        caption={t('Begin')}
                                        defVal={
                                            theClass.ini
                                                ? format(
                                                      parseISO(theClass.ini),
                                                      'HH:mm',
                                                  )
                                                : ''
                                        }
                                        min="00:00:00"
                                        max="23:55:00"
                                        interval={5}
                                        onChange={setTime}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <SttFormTime
                                        name="end"
                                        caption={t('Until')}
                                        defVal={
                                            theClass.end
                                                ? format(
                                                      parseISO(theClass.end),
                                                      'HH:mm',
                                                  )
                                                : ''
                                        }
                                        min="00:00:00"
                                        max="23:55:00"
                                        interval={5}
                                        onChange={setTime}
                                    />
                                </Grid>
                                {!theClass.id && (
                                    <Grid size={{ xs: 12 }}>
                                        <SttSelectWeekdays
                                            name="weekdays"
                                            selected={theClass.weekdays}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </div>
                    </>
                }
            />
            {derivedActivity && (
                <SttFullDialog
                    title={t('generic.assistences')}
                    open={dialog}
                    onClose={() => setDialog(false)}
                >
                    <Box className="p-4">
                        <SttFormAttendance activity={derivedActivity} />
                    </Box>
                </SttFullDialog>
            )}
        </>
    );
}
